import { useMemo } from 'react';
import { Link } from 'react-router';
import { Button } from '@repo/ui/components/button';
import { urlCashierMode, urlTournamentList, urlSales, urlCreateProduct } from '@/urls';
import { Monitor, Trophy, Package, BarChart3 } from 'lucide-react';
import type { Club } from '@repo/shared-types';
import type { LucideIcon } from 'lucide-react';

interface QuickAction {
  title: string;
  url: string;
  icon: LucideIcon;
  requiredFeatures: string[];
}

function getQuickActions(clubId: string): QuickAction[] {
  return [
    {
      title: 'Modo caja',
      url: urlCashierMode(clubId),
      icon: Monitor,
      requiredFeatures: ['pointsOfSale'],
    },
    {
      title: 'Crear torneo',
      url: urlTournamentList(clubId),
      icon: Trophy,
      requiredFeatures: ['tournaments'],
    },
    {
      title: 'Crear producto',
      url: urlCreateProduct(clubId),
      icon: Package,
      requiredFeatures: ['pointsOfSale'],
    },
    {
      title: 'Ver resumen de ventas',
      url: urlSales(clubId),
      icon: BarChart3,
      requiredFeatures: ['pointsOfSale'],
    },
  ];
}

export function QuickActions({ club }: { club: Club }) {
  const actions = useMemo(() => getQuickActions(club.id), [club.id]);

  const filteredActions = useMemo(() => {
    if (!club || !club.features) return [];
    return actions.filter((action) =>
      action.requiredFeatures.every(
        (feature) =>
          feature in club.features && club.features[feature as keyof typeof club.features]
      )
    );
  }, [actions, club.features]);

  if (filteredActions.length === 0) {
    return null;
  }

  return (
    <div>
      <h2 className="text-lg font-semibold mb-3">Acciones rápidas</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {filteredActions.map((action) => {
          const Icon = action.icon;
          return (
            <Button key={action.title} variant="outline" className="flex gap-2 py-8" asChild>
              <Link to={action.url} viewTransition>
                <Icon className="size-4" />
                <span>{action.title}</span>
              </Link>
            </Button>
          );
        })}
      </div>
    </div>
  );
}
